import React from 'react';
import SEO from '../components/SEO';
import { Shield, Mail, Phone, MapPin, FileText, CheckCircle } from 'lucide-react';
import { contactInfo } from '../data';

export default function Privatliv() {
  return (
    <>
      <SEO 
        title="Privatlivspolitik | Syddansk Malerfirma"
        description="Læs om, hvordan Syddansk Malerfirma behandler og beskytter dine personoplysninger i overensstemmelse med GDPR."
      />

      <section className="pt-16 pb-24 bg-slate-50">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8 lg:px-12">
          
          {/* Header */}
          <div className="mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-[#0b4e47] bg-teal-50 px-3 py-1.5 rounded-full border border-teal-100">
              Sikkerhed & Data
            </span>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-800 tracking-tight leading-[1.15] mt-4 mb-4">
              Privatlivspolitik
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl">
              for Syddansk Malerfirma v/ Mette Frank
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12 items-start">
            
            {/* Left Column: Quick Info Card */}
            <div className="lg:col-span-1 space-y-6 lg:sticky lg:top-28">
              <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center text-teal-900">
                    <Shield className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-lg">Kort fortalt</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">
                  Vi passer godt på dine personoplysninger. Her kan du læse, hvordan vi behandler de data, vi modtager, når du bruger vores hjemmeside og kontaktformular.
                </p>
                
                <div className="border-t border-slate-100 pt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-700">Ingen videresalg af data</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-700">Kun pålidelige partnere</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-700">Fuld indsigt og kontrol</p>
                  </div>
                </div>
              </div>

              {/* Contact Card */}
              <div className="bg-teal-950 text-white p-8 rounded-3xl shadow-sm">
                <h3 className="font-bold text-lg mb-4 text-orange-400">Har du spørgsmål?</h3>
                <p className="text-teal-100/80 text-sm leading-relaxed mb-6">
                  Hvis du vil gøre brug af dine rettigheder eller har spørgsmål til vores datapolitik, er du altid velkommen til at kontakte os.
                </p>
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-3 text-white hover:text-orange-400 transition-colors py-2 border-b border-teal-900"
                >
                  <Mail className="w-4 h-4 text-orange-400" />
                  <span className="text-sm font-medium truncate">{contactInfo.email}</span>
                </a>
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="flex items-center gap-3 text-white hover:text-orange-400 transition-colors py-2"
                >
                  <Phone className="w-4 h-4 text-orange-400" />
                  <span className="text-sm font-medium">{contactInfo.phone}</span>
                </a>
              </div>
            </div>

            {/* Right Column: Detailed Policy Content */}
            <div className="lg:col-span-2 bg-white p-8 sm:p-12 rounded-3xl border border-slate-200 shadow-sm space-y-10">
              
              {/* Section 1 */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-[#0b4e47]">
                  <span className="text-lg font-black bg-teal-50 w-8 h-8 rounded-lg flex items-center justify-center border border-teal-100">1</span>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Dataansvarlig (Hvem er vi?)</h2>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  Det er os, der er ansvarlige for behandlingen af dine personoplysninger:
                </p>
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Firmanavn</p>
                    <p className="font-bold text-slate-800 mt-0.5">Syddansk Malerfirma v/ Mette Frank</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">CVR-nummer</p>
                    <p className="font-bold text-slate-800 mt-0.5">{contactInfo.cvr}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Adresse</p>
                    <p className="font-bold text-slate-800 mt-0.5">{contactInfo.address}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">E-mail</p>
                    <a href={`mailto:${contactInfo.email}`} className="font-bold text-teal-900 hover:underline mt-0.5 block">{contactInfo.email}</a>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Telefonnummer</p>
                    <a href={`tel:${contactInfo.phone}`} className="font-bold text-teal-900 hover:underline mt-0.5 block">{contactInfo.phone}</a>
                  </div>
                </div>
              </div>

              {/* Section 2 */}
              <div className="space-y-4 border-t border-slate-100 pt-8">
                <div className="flex items-center gap-3 text-[#0b4e47]">
                  <span className="text-lg font-black bg-teal-50 w-8 h-8 rounded-lg flex items-center justify-center border border-teal-100">2</span>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Hvilke oplysninger indsamler vi, og hvorfor?</h2>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  Når du bruger vores kontaktformular til at bede om et malertilbud eller stille spørgsmål, indsamler vi de oplysninger, du selv indtaster:
                </p>
                <ul className="grid sm:grid-cols-2 gap-3 pl-2">
                  {[
                    "Navn",
                    "E-mailadresse",
                    "Telefonnummer",
                    "Adresse (så vi ved, hvor malerarbejdet skal udføres)",
                    "Beskrivelse af din maleropgave"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-slate-700 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-slate-600 leading-relaxed pt-2">
                  Formålet er udelukkende at kunne kontakte dig, svare på din henvendelse og udarbejde et uforpligtende malertilbud til dig.
                </p>
              </div>

              {/* Section 3 */}
              <div className="space-y-4 border-t border-slate-100 pt-8">
                <div className="flex items-center gap-3 text-[#0b4e47]">
                  <span className="text-lg font-black bg-teal-50 w-8 h-8 rounded-lg flex items-center justify-center border border-teal-100">3</span>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Lovligt grundlag for behandlingen</h2>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  Vi behandler dine oplysninger baseret på GDPR-reglerne om:
                </p>
                <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                  <p className="font-bold text-slate-800 text-sm mb-1">Opfyldelse af en aftale:</p>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Vi har brug for dine oplysninger for at kunne give dig det tilbud, du selv har bedt om.
                  </p>
                </div>
              </div>

              {/* Section 4 */}
              <div className="space-y-4 border-t border-slate-100 pt-8">
                <div className="flex items-center gap-3 text-[#0b4e47]">
                  <span className="text-lg font-black bg-teal-50 w-8 h-8 rounded-lg flex items-center justify-center border border-teal-100">4</span>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Hvem deler vi dine oplysninger med?</h2>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  Vi sælger eller videregiver aldrig dine oplysninger til tredjeparter. Vi bruger dog pålidelige samarbejdspartnere (databehandlere), som hjælper os med at drive vores forretning – herunder:
                </p>
                <ul className="space-y-3 pl-2">
                  <li className="flex items-start gap-2.5 text-slate-700 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                    <span>Vores webhotel (hvor hjemmesiden og kontaktformularen ligger).</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-slate-700 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                    <span>Vores it- og e-mailleverandør.</span>
                  </li>
                </ul>
                <p className="text-slate-600 leading-relaxed pt-2">
                  Disse parter behandler kun oplysningerne på vores instruks og må ikke bruge dem til egne formål.
                </p>
              </div>

              {/* Section 5 */}
              <div className="space-y-4 border-t border-slate-100 pt-8">
                <div className="flex items-center gap-3 text-[#0b4e47]">
                  <span className="text-lg font-black bg-teal-50 w-8 h-8 rounded-lg flex items-center justify-center border border-teal-100">5</span>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Hvor længe gemmer vi dine data?</h2>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  Vi gemmer kun dine oplysninger, så længe det er nødvendigt:
                </p>
                <div className="space-y-4">
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                    <p className="font-bold text-slate-800 text-sm mb-1">Hvis du takker NEJ til malertilbuddet:</p>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      Vi sletter dine oplysninger fra kontaktformularen senest 6 måneder efter vores sidste kontakt.
                    </p>
                  </div>
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                    <p className="font-bold text-slate-800 text-sm mb-1">Hvis du takker JA og bliver kunde:</p>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      Vi gemmer dine oplysninger (fakturaer og arbejdshistorik) i 5 år efter udgangen af det pågældende regnskabsår. Det er vi lovmæssigt forpligtet til ifølge den danske bogføringslov.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 6 */}
              <div className="space-y-4 border-t border-slate-100 pt-8">
                <div className="flex items-center gap-3 text-[#0b4e47]">
                  <span className="text-lg font-black bg-teal-50 w-8 h-8 rounded-lg flex items-center justify-center border border-teal-100">6</span>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Dine rettigheder</h2>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  Du har ifølge GDPR en række rettigheder. Du kan til enhver tid kontakte os for at:
                </p>
                <ul className="space-y-3 pl-2 mb-4">
                  <li className="flex items-start gap-2.5 text-slate-700 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                    <span>Få indsigt i, hvilke oplysninger vi har registreret om dig.</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-slate-700 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                    <span>Få rettet forkerte oplysninger.</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-slate-700 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                    <span>Få slettet dine oplysninger (såfremt vi ikke er lovmæssigt forpligtet til at gemme dem pga. bogføringsloven).</span>
                  </li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  Hvis du vil gøre brug af dine rettigheder, skal du blot sende os en e-mail på <a href={`mailto:${contactInfo.email}`} className="text-teal-900 font-bold hover:underline">{contactInfo.email}</a>.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Du har også ret til at klage over vores behandling af dine data til Datatilsynet (<a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="text-teal-900 font-bold hover:underline">www.datatilsynet.dk</a>).
                </p>
              </div>

            </div>

          </div>

        </div>
      </section>
    </>
  );
}
